import { useState } from "react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  usePointsConfigurations,
  useSubmitPointsConfiguration,
  useDeactivatePointsConfiguration,
} from "../hooks/usePointsConfig";
import type { PointsConfiguration } from "../services/pointsConfigApi";
import { PointsConfigFormDialog } from "../components/PointsConfigFormDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";

const statusVariant = (status: PointsConfiguration["status"]) => {
  switch (status) {
    case "Approved":
      return "secondary" as const;
    case "Rejected":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
};

const formatKes = (n: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(n);
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-KE", { dateStyle: "medium" }) : "—";

export const PointsConfigListPage = () => {
  const notification = useNotification();
  const { hasPermission } = useAuthStore();
  const listQuery = usePointsConfigurations();
  const submitMutation = useSubmitPointsConfiguration();
  const deactivateMutation = useDeactivatePointsConfiguration();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PointsConfiguration | null>(null);

  const canEdit = hasPermission("loyalty.config_request");
  const canApprove = hasPermission("loyalty.config_approve");

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (config: PointsConfiguration) => {
    setEditing(config);
    setFormOpen(true);
  };

  const submit = (config: PointsConfiguration) =>
    submitMutation
      .mutateAsync(config.id)
      .then(() => notification.success("Submitted for approval."))
      .catch(() => notification.error("Failed to submit configuration."));

  const deactivate = (config: PointsConfiguration) =>
    deactivateMutation
      .mutateAsync(config.id)
      .then(() => notification.success(`"${config.name}" deactivated.`))
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Failed to deactivate configuration.");
      });

  const configs = listQuery.data ?? [];
  // Only one configuration may be live at a time (enforced server-side at approval).
  // Steer makers away from creating a doomed second config while one is active.
  const activeConfig = configs.find((c) => c.status === "Approved" && c.isActiveFlag);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Loyalty Points Configuration</h1>
          <p className="text-sm text-muted-foreground">
            Define earn/redeem rules. Changes go through maker-checker approval before they affect customers.
          </p>
        </div>
        {canEdit &&
          (activeConfig ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* span wrapper: a disabled button emits no pointer events for the tooltip */}
                  <span tabIndex={0}>
                    <Button disabled>New configuration</Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  A configuration ("{activeConfig.name}") is already active. Deactivate it before
                  creating a new one.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button onClick={openNew}>New configuration</Button>
          ))}
      </div>

      {listQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : configs.length === 0 ? (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          No configurations yet.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Earn rate</TableHead>
              <TableHead>Point value</TableHead>
              <TableHead>Min redeem</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {configs.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">
                  {c.name}
                  {!c.isActiveFlag && <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                  {c.status === "Rejected" && c.rejectionReason && (
                    <div className="text-[11px] text-muted-foreground mt-1 max-w-xs truncate" title={c.rejectionReason}>
                      {c.rejectionReason}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {c.earnRatePoints} pt / {formatKes(c.earnRatePerAmount)}
                </TableCell>
                <TableCell>{formatKes(c.pointValueKes)}</TableCell>
                <TableCell>{c.minRedeemablePoints}</TableCell>
                <TableCell className="text-xs">
                  {formatDate(c.effectiveFrom)} → {c.effectiveTo ? formatDate(c.effectiveTo) : "open"}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {canEdit && (c.status === "Draft" || c.status === "Rejected") && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                        Edit
                      </Button>
                      {c.status === "Draft" && (
                        <Button size="sm" onClick={() => submit(c)} disabled={submitMutation.isPending}>
                          Submit
                        </Button>
                      )}
                    </>
                  )}
                  {canApprove && c.status === "Approved" && c.isActiveFlag && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deactivate(c)}
                      disabled={deactivateMutation.isPending}
                    >
                      Deactivate
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PointsConfigFormDialog open={formOpen} onOpenChange={setFormOpen} existing={editing} />
    </div>
  );
};
